import { useForm, useGo, useOne } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FormField, FormHeader, FormStickyActions, FormSpacer } from "@/components/forms";
import { notify } from '@/lib/notify';
import { useServerErrors } from "@/hooks/useServerErrors";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { type Language, type CreateLanguageInput, LANGUAGE_FLAGS } from './types';

interface LanguagesFormProps {
  mode: "create" | "edit";
}

/**
 * Languages create/edit form
 */
export function LanguagesForm({ mode }: LanguagesFormProps) {
  const go = useGo();
  const params = useParams<{ id: string }>();
  const isEdit = mode === "edit";

  // Load existing language for edit mode (public endpoint for reading)
  const { result: languageData, query: languageQuery } = useOne<Language>({
    resource: "languages",
    id: params.id ?? "",
    queryOptions: {
      enabled: isEdit && !!params.id,
    },
  });

  const isLoadingLanguage = languageQuery.isLoading;

  // Хук для обработки ошибок сервера
  const serverErrors = useServerErrors();

  const { onFinish, mutation } = useForm({
    resource: "admin/languages",
    action: isEdit ? "edit" : "create",
    redirect: false,
    id: params.id,
    successNotification: () => ({
      message: isEdit ? "Язык обновлён" : "Язык создан",
      type: "success",
    }),
    // Отключаем стандартное уведомление об ошибке - обрабатываем сами
    errorNotification: false,
    onMutationSuccess: () => {
      go({ to: "/languages" });
    },
    onMutationError: (error) => {
      // Обрабатываем ошибки сервера
      serverErrors.setErrorsFromResponse(error);
    },
  });

  const initialState: CreateLanguageInput = languageData
    ? {
        code: languageData.code,
        name: languageData.name,
        nativeName: languageData.nativeName,
        flagEmoji: languageData.flagEmoji || languageData.flag || "",
      }
    : {
        code: "",
        name: "",
        nativeName: "",
        flagEmoji: "",
      };

  const formKey = isEdit ? `edit-${languageData?.code ?? "loading"}` : "create";

  if (isEdit && isLoadingLanguage) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <LanguagesFormFields
      key={formKey}
      isEdit={isEdit}
      initialState={initialState}
      isSubmitting={mutation.isPending}
      serverErrors={serverErrors}
      onFinish={async (values) => {
        // Очищаем предыдущие ошибки перед отправкой
        serverErrors.clearAllErrors();
        await onFinish(values);
      }}
      onCancel={() => go({ to: "/languages" })}
    />
  );
}

interface LanguagesFormFieldsProps {
  isEdit: boolean;
  initialState: CreateLanguageInput;
  isSubmitting: boolean;
  serverErrors: ReturnType<typeof useServerErrors>;
  onFinish: (values: CreateLanguageInput) => Promise<void>;
  onCancel: () => void;
}

function LanguagesFormFields({
  isEdit,
  initialState,
  isSubmitting,
  serverErrors,
  onFinish,
  onCancel,
}: LanguagesFormFieldsProps) {
  const [formData, setFormData] = useState<CreateLanguageInput>(initialState);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Клиентская валидация
    if (!formData.code || !formData.name || !formData.nativeName || !formData.flagEmoji) {
      notify.error("Заполните все обязательные поля");
      return;
    }

    if (!isEdit && formData.code.length !== 2) {
      notify.error("Код языка должен содержать 2 символа");
      return;
    }

    await onFinish(formData);
  };

  const handleChange = (field: keyof CreateLanguageInput, value: string) => {
    // Очищаем ошибку поля при изменении
    serverErrors.clearFieldError(field);

    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "code" && !prev.flagEmoji) {
        const flag = LANGUAGE_FLAGS[value.toLowerCase()];
        if (flag) {
          return { ...next, flagEmoji: flag };
        }
      }
      return next;
    });
  };

  return (
    <div className="space-y-6 pb-24">
      <FormHeader
        title={isEdit ? "Редактировать язык" : "Добавить язык"}
        description={
          isEdit
            ? `Редактирование языка ${formData.name}`
            : "Добавление нового языка в систему"
        }
        onBack={onCancel}
        sticky
        actions={null}
      />

      <form onSubmit={handleSubmit}>
        {/* Общая ошибка (не привязанная к полю) */}
        {serverErrors.generalError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverErrors.generalError}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Основные данные</CardTitle>
            <CardDescription>Введите информацию о языке</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Код языка"
                name="code"
                required
                error={serverErrors.getFieldError("code")}
                hint="ISO 639-1 код (2 символа)"
              >
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) =>
                    handleChange("code", e.target.value.toLowerCase())
                  }
                  placeholder="ru"
                  maxLength={2}
                  disabled={isEdit}
                  className="font-mono"
                />
              </FormField>

              <FormField
                label="Флаг (эмодзи)"
                name="flagEmoji"
                required
                error={serverErrors.getFieldError("flagEmoji")}
              >
                <Input
                  id="flagEmoji"
                  value={formData.flagEmoji}
                  onChange={(e) => handleChange("flagEmoji", e.target.value)}
                  placeholder="🇷🇺"
                  className="text-2xl"
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Название (English)"
                name="name"
                required
                error={serverErrors.getFieldError("name")}
              >
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Russian"
                />
              </FormField>

              <FormField
                label="Название (родное)"
                name="nativeName"
                required
                error={serverErrors.getFieldError("nativeName")}
              >
                <Input
                  id="nativeName"
                  value={formData.nativeName}
                  onChange={(e) => handleChange("nativeName", e.target.value)}
                  placeholder="Русский"
                />
              </FormField>
            </div>

            {formData.code && formData.flagEmoji && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Превью:</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{formData.flagEmoji}</span>
                  <div>
                    <p className="font-medium">{formData.name || "—"}</p>
                    <p className="text-sm text-muted-foreground">
                      {formData.nativeName || "—"} ({formData.code})
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <FormStickyActions
          primaryLabel={isEdit ? "Сохранить" : "Создать"}
          secondaryLabel="Отмена"
          onPrimary={handleSubmit}
          onSecondary={onCancel}
          isPending={isSubmitting}
          pendingLabel={isEdit ? "Сохранение..." : "Создание..."}
        />
      </form>

      <FormSpacer />
    </div>
  );
}

/**
 * Create page wrapper
 */
export function LanguagesCreate() {
  return <LanguagesForm mode="create" />;
}

/**
 * Edit page wrapper
 */
export function LanguagesEdit() {
  return <LanguagesForm mode="edit" />;
}
